import {
  Component,
  Input,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  PLATFORM_ID,
  Inject,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-image-zoom',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      #zoomContainer
      class="relative overflow-hidden border border-gray-300 cursor-zoom-in bg-white"
      [class]="containerClasses"
      (mouseenter)="onMouseEnter()"
      (mouseleave)="onMouseLeave()"
      (mousemove)="onMouseMove($event)"
      (wheel)="onWheel($event)"
    >
      <!-- Indicador de zoom -->
      @if (isZoomed && currentZoom > 1) {
      <div
        class="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded z-10 fs-condensed"
      >
        {{ (currentZoom * 100).toFixed(0) }}%
      </div>
      }
      <img
        #zoomImage
        [src]="imageSrc"
        [alt]="imageAlt"
        class="w-full h-full object-contain transition-all duration-500 ease-out absolute top-0 left-0"
        [style.transform]="imageTransform"
        [style.transformOrigin]="transformOrigin"
      />
    </div>
  `,
  styles: [],
})
export class ImageZoomComponent implements AfterViewInit, OnDestroy {
  @Input() imageSrc: string = '';
  @Input() imageAlt: string = '';
  @Input() zoomLevel: number = 2;
  @Input() minZoom: number = 1.2;
  @Input() maxZoom: number = 4;
  @Input() width: string = 'w-96';
  @Input() height: string = 'h-96';

  @ViewChild('zoomContainer', { static: false })
  zoomContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('zoomImage', { static: false })
  zoomImage!: ElementRef<HTMLImageElement>;

  imageTransform: string = 'scale(1)';
  transformOrigin: string = 'center center';
  isZoomed: boolean = false;
  currentZoom: number = 1;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  get containerClasses(): string {
    return `${this.width} ${this.height}`;
  }

  ngAfterViewInit(): void {
    // Componente inicializado
  }

  ngOnDestroy(): void {
    // Cleanup si es necesario
  }

  onMouseEnter(): void {
    this.isZoomed = true;
    this.currentZoom = this.zoomLevel;
    // Transición suave de entrada
    setTimeout(() => {
      this.updateZoom();
    }, 50);
  }

  onMouseLeave(): void {
    this.isZoomed = false;
    // Transición suave de salida
    this.currentZoom = 1;
    this.updateZoom();
    this.transformOrigin = 'center center';
  }

  onWheel(event: WheelEvent): void {
    if (!this.isZoomed) return;

    event.preventDefault();

    // Determinar dirección del scroll
    const delta = event.deltaY > 0 ? -0.2 : 0.2;

    // Calcular nuevo zoom
    this.currentZoom = Math.max(
      this.minZoom,
      Math.min(this.maxZoom, this.currentZoom + delta)
    );

    // Aplicar zoom con transición suave
    this.updateZoom();
  }

  private updateZoom(): void {
    this.imageTransform = `scale(${this.currentZoom})`;
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isZoomed || !this.zoomContainer) return;

    const container = this.zoomContainer.nativeElement;
    const rect = container.getBoundingClientRect();

    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    // Transición suave del seguimiento del cursor (solo en el navegador)
    if (isPlatformBrowser(this.platformId)) {
      requestAnimationFrame(() => {
        this.transformOrigin = `${x}% ${y}%`;
      });
    } else {
      this.transformOrigin = `${x}% ${y}%`;
    }
  }
}
